import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { PageHeader, ExportBar } from "@/components/EntityPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";

function ClientForm({ initial, onSubmit }) {
  const [f, setF] = useState(initial || {});
  const [users, setUsers] = useState([]);
  const [copyWA, setCopyWA] = useState(!!initial && initial.mobile && initial.mobile === initial.whatsapp);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  useEffect(() => {
    api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.role === "client"))).catch(() => {});
  }, []);
  function onMobile(e) {
    const v = e.target.value;
    setF((s) => ({ ...s, mobile: v, whatsapp: copyWA ? v : s.whatsapp }));
  }
  function toggleCopy(v) {
    setCopyWA(!!v); if (v) setF((s) => ({ ...s, whatsapp: s.mobile || s.whatsapp }));
  }
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-[10px] uppercase text-muted-foreground">Client name</Label>
          <Input required value={f.name || ""} onChange={set("name")} data-testid="client-name" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Company</Label>
          <Input value={f.company || ""} onChange={set("company")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Email</Label>
          <Input type="email" value={f.email || ""} onChange={set("email")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Mobile</Label>
          <Input value={f.mobile || ""} onChange={onMobile} data-testid="client-mobile" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">WhatsApp</Label>
          <Input value={f.whatsapp || ""} onChange={set("whatsapp")} disabled={copyWA} data-testid="client-whatsapp" className={`bg-[hsl(224_40%_12%)] mt-1 ${copyWA ? "opacity-70" : ""}`}/>
          <label className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-300 cursor-pointer">
            <input type="checkbox" checked={copyWA} onChange={(e) => toggleCopy(e.target.checked)} data-testid="client-wa-same" className="accent-amber-500 h-3.5 w-3.5"/>
            Same as mobile — auto-copy
          </label>
        </div>
        <div className="col-span-2"><Label className="text-[10px] uppercase text-muted-foreground">Address</Label>
          <Input value={f.address || ""} onChange={set("address")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div className="col-span-2">
          <Label className="text-[10px] uppercase text-amber-300/80">Link to login account (role: client)</Label>
          <select value={f.linked_user_id || ""} onChange={(e) => setF({ ...f, linked_user_id: e.target.value || null })}
            data-testid="client-linked-user"
            className="w-full mt-1 h-9 rounded-md bg-[hsl(224_40%_12%)] border border-border text-sm px-2">
            <option value="">— not linked —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
          </select>
          <div className="text-[10px] text-muted-foreground mt-1">Pick the login account created in Users &amp; Roles → Customers. Once linked, that account sees this client's trips on the portal.</div>
        </div>
      </div>
      <div className="flex justify-end"><Button type="submit" data-testid="client-save" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">{initial ? "Update" : "Create"}</Button></div>
    </form>
  );
}

export default function Clients() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() { const { data } = await api.get("/clients"); setRows(data); }
  useEffect(() => { load(); }, []);
  async function save(f) {
    try {
      if (editing) await api.put(`/clients/${editing.id}`, f); else await api.post("/clients", f);
      toast.success("Saved"); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }
  async function del(id) {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/clients/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }
  const filtered = rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Clients" subtitle="Customer directory. All client details and contacts."
        right={<>
          <ExportBar rows={filtered.map((c) => ({ name: c.name, company: c.company, email: c.email, mobile: c.mobile, whatsapp: c.whatsapp, address: c.address }))} filename="clients" whatsappText={`Clients: ${filtered.length}`}/>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild><Button data-testid="add-clients-btn" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold gap-2"><Plus className="w-4 h-4"/> New client</Button></DialogTrigger>
            <DialogContent className="bg-[hsl(224_45%_9%)] border-border max-w-xl">
              <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit" : "New"} client</DialogTitle></DialogHeader>
              <ClientForm initial={editing} onSubmit={save}/>
            </DialogContent>
          </Dialog>
        </>}
      />
      <Card className="bg-[hsl(224_45%_9%)] border-border">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <Search className="w-4 h-4 text-muted-foreground"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter clients…" data-testid="filter-input" className="bg-transparent border-0 focus-visible:ring-0 h-8"/>
          <div className="text-xs text-muted-foreground">{filtered.length} of {rows.length}</div>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground bg-[hsl(224_40%_11%)]">
            {["Client","Company","Email","Mobile","WhatsApp","Address","Actions"].map((h) => <th key={h} className="text-left p-3">{h}</th>)}
          </tr></thead>
          <tbody>
            {filtered.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No records</td></tr> :
              filtered.map((c) => (
                <tr key={c.id} className="border-t border-border">
                  <td className="p-3">{c.name}</td>
                  <td className="p-3">{c.company || "—"}</td>
                  <td className="p-3 font-mono text-xs">{c.email || "—"}</td>
                  <td className="p-3 font-mono text-xs">{c.mobile || "—"}</td>
                  <td className="p-3 font-mono text-xs">{c.whatsapp || "—"}</td>
                  <td className="p-3 text-xs">{c.address || "—"}</td>
                  <td className="p-3"><div className="inline-flex gap-1">
                    <Button variant="ghost" size="icon" data-testid={`edit-client-${c.id}`} className="h-8 w-8" onClick={() => { setEditing(c); setOpen(true); }}><Pencil className="w-3.5 h-3.5"/></Button>
                    <Button variant="ghost" size="icon" data-testid={`del-client-${c.id}`} className="h-8 w-8 hover:text-red-400" onClick={() => del(c.id)}><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div></td>
                </tr>
              ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
